import { useLingui } from "@lingui/react/macro";
import { useLocale } from "@offstreetparking/design-system/providers";
import { isAdditionalInfoEmailLabel } from "@offstreetparking/domain/value/email";
import { getCountryCurrency } from "@offstreetparking/geography";
import { useMemo } from "react";
import { usePortalFlowHost } from "../../../../host";
import { useParker } from "../../../../providers/parker";
import { type PermitFormData, stepIds, usePermitFormData, usePermitSteps } from "../../PermitContextProvider";
import { AdditionalInfoForm } from "../../components/AdditionalInfoForm";
import { ConfirmationStep } from "../../components/ConfirmationStep";
import { EnterCodeForm } from "../../components/EnterCodeForm";
import { ParkingDetailsForm } from "../../components/ParkingDetailsForm/ParkingDetailsForm";
import { ParkingTimeForm } from "../../components/ParkingTimeForm";
import { PaymentForm } from "../../components/PaymentForm";
import { isChoiceIneligible } from "../../isChoiceIneligible";
import { splitChoicesByAvailability } from "../../splitChoicesByAvailability";
import type { OverrideNotices, PermitTypeDto, TenantInfo } from "../../types";
import type { usePermitInvoice } from "../usePermitInvoice";
import { usePermitTermsStep } from "./usePermitTermsStep";
type UseCreatePermitStepsProps = {
	formId: string;
	permitType: PermitTypeDto;
	overrideNotices?: OverrideNotices;
	onSelectOverridePermitType?: (permitTypeId: number) => void;
	preSelectedLocationId?: number;
	savedPermitLookupId?: string;
	tenantId?: number;
	registrationInstructions?: string;
	selectedTenant?: TenantInfo;
	setDuplicatedPermit: ({
		plate,
		permitIntervals,
		publicIdentifier,
	}: {
		plate: string;
		permitIntervals: PermitFormData["permitIntervals"];
		publicIdentifier: string;
	}) => void;
	invoice: ReturnType<typeof usePermitInvoice>["invoice"];
	onSubmit: (e: PermitFormData) => void;
};
export const useCreatePermitSteps = ({
	formId,
	permitType,
	overrideNotices,
	onSelectOverridePermitType,
	preSelectedLocationId,
	savedPermitLookupId,
	tenantId,
	registrationInstructions,
	selectedTenant,
	setDuplicatedPermit,
	invoice,
	onSubmit,
}: UseCreatePermitStepsProps) => {
	const permit = usePermitFormData();
	const permitSteps = usePermitSteps();
	const { locale } = useLocale();
	const { t } = useLingui();
	const currency = getCountryCurrency(permitType.company.address?.country ?? undefined);
	const termsStep = usePermitTermsStep({
		formId,
		permitTypeId: permitType.permitTypeId,
		parkerTermsVersionIds: permit?.parkerTermsVersionIds,
		onSubmit,
	});
	const { isLoggedIn } = useParker();
	const PaymentStepComponent = usePortalFlowHost().PaymentSlot ?? PaymentForm;
	const firstInterval = permit.permitIntervals?.[0];
	const lastInterval = permit.permitIntervals?.[permit.permitIntervals?.length - 1];
	const isSmsEnabled = Boolean(
		permitType.company.productFeatures?.sms &&
			permitType.smsExpiryNotifications &&
			(selectedTenant === undefined || selectedTenant.smsExpiryNotifications),
	);
	const isSmsRequired = isSmsEnabled && permitType.wizardSettings.isSmsRequired;
	return useMemo(() => {
		const emailAdditionalInfo = permitType.wizardSettings.additionalInformation.fields?.filter((info) =>
			isAdditionalInfoEmailLabel(info.label),
		);
		const prepopulatedFields = permit.email
			? emailAdditionalInfo.map((field) => ({
					permitFieldId: field.id,
					value: permit.email!,
					label: field.label,
				}))
			: [];
		const allChoices = permitType.wizardSettings.choices ?? [];
		const shouldHideIneligibleChoices = permitType.wizardSettings.hideFutureParkingOptions;
		const visibleChoices = shouldHideIneligibleChoices
			? allChoices.filter((choice) => !isChoiceIneligible(choice))
			: allChoices;
		const { availableChoices, upcomingChoices } = splitChoicesByAvailability(visibleChoices);
		const hasWindowOpenChoice = availableChoices.some(
			(choice) => choice.availability !== "outside-recurring-window",
		);
		const allSteps = [
			{
				id: stepIds.parkingTime,
				title: t`Parking Time`,
				component: (
					<ParkingTimeForm
						formId={formId}
						initialValues={{
							parkingChoiceId: permit?.parkingChoiceId ?? undefined,
							locationIds: permit?.locationIds ?? [],
							permitIntervals: permit?.permitIntervals,
						}}
						onSubmit={onSubmit}
						availableChoices={availableChoices}
						upcomingChoices={upcomingChoices}
						overrideNotices={overrideNotices}
						onSelectOverridePermitType={onSelectOverridePermitType}
						timezone={permitType.timezone}
						preSelectedLocationId={preSelectedLocationId}
					/>
				),
				primaryActionLabel: t`Next`,
				hidePrimaryAction: !hasWindowOpenChoice && upcomingChoices.length === 0,
			},
			{
				id: stepIds.parkingDetails,
				title: t`Permit Details`,
				component: (
					<ParkingDetailsForm
						formId={formId}
						permitType={permitType}
						invoice={invoice}
						onSubmit={onSubmit}
						isEmailRequired={permitType.wizardSettings.isEmailRequired}
						initialValues={{
							vehicles: permit.vehicles,
							permitIntervals: permit?.permitIntervals,
							email: permit?.email,
							phoneNumber: permit?.phoneNumber,
							locationIds: permit?.locationIds ?? [],
						}}
						isLoggedIn={isLoggedIn}
						currency={currency}
						registrationInstructions={registrationInstructions}
						isSmsEnabled={isSmsEnabled}
						isSmsRequired={isSmsRequired}
					/>
				),
				primaryActionLabel: t`Next`,
			},
			{
				id: stepIds.additionalInfo,
				title: t`Additional Info`,
				component: (
					<AdditionalInfoForm
						formId={formId}
						additionalInfo={permitType.wizardSettings.additionalInformation.fields ?? []}
						onSubmit={onSubmit}
						initialValues={{
							fields: permit?.fields ?? prepopulatedFields,
						}}
					/>
				),
				primaryActionLabel: t`Next`,
			},
			{
				id: stepIds.code,
				title: t`Code`,
				component: (
					<EnterCodeForm
						formId={formId}
						onSubmit={onSubmit}
						permitTypeId={permit?.parkingChoiceId ?? permitType.permitTypeId}
						isEvent={permitType.isEvent}
						initialValue={{
							code: permit?.code,
						}}
						codeLabel={permitType.context?.code?.label}
						codeDescription={permitType.context?.code?.description}
						permitIntervals={permit?.permitIntervals}
					/>
				),
				primaryActionLabel: t`Next`,
			},
			{
				id: stepIds.terms,
				title: termsStep.title,
				component: termsStep.component,
				isLastTerms: termsStep.isLastTerms,
				hasAcceptedAllTerms: termsStep.hasAcceptedAllTerms,
				primaryActionLabel: termsStep.primaryActionLabel,
				secondaryAction: termsStep.secondaryAction,
			},
			{
				id: stepIds.payment,
				title: t`Payment`,
				component: (
					<PaymentStepComponent
						formId={formId}
						onSubmit={onSubmit}
						setDuplicatedPermit={setDuplicatedPermit}
						permitType={permitType}
						locationLookupId={savedPermitLookupId}
						tenantId={tenantId}
						vehicles={permit.vehicles}
						time={{
							startDateTime: firstInterval?.startDateTimeISO!,
							endDateTime: lastInterval?.endDateTimeISO ?? undefined,
							timezone: permitType.timezone,
						}}
						invoice={invoice}
						email={permit.email}
						currency={currency}
						locale={locale}
					/>
				),
				primaryActionLabel: t`Pay`,
			},
			{
				id: stepIds.confirmation,
				component: (
					<ConfirmationStep
						publicIdentifier={permit.publicIdentifier!}
						publicAccessToken={permit.publicAccessToken}
						fields={permit.fields}
						email={permit.email}
						phoneNumber={permit.phoneNumber}
						addAnotherOption={
							availableChoices.filter(
								(x) =>
									x.capacity !== "full" &&
									x.override?.state !== "disabled" &&
									x.permitTypeId !== permit.parkingChoiceId,
							).length > 0
								? () => {
										window.history.scrollRestoration = "manual";
										window.location.reload();
									}
								: undefined
						}
					/>
				),
			},
		];
		return allSteps.filter((step) => permitSteps.includes(step.id));
	}, [
		formId,
		permitType,
		permit,
		locale,
		currency,
		onSubmit,
		setDuplicatedPermit,
		t,
		permitSteps,
		firstInterval,
		lastInterval,
		termsStep,
		overrideNotices,
		onSelectOverridePermitType,
		preSelectedLocationId,
		savedPermitLookupId,
		tenantId,
		isSmsEnabled,
		isSmsRequired,
		invoice,
		PaymentStepComponent,
		isLoggedIn,
		registrationInstructions,
	]);
};
